(() => {
  const money = (cents) => `LE ${(Number(cents || 0) / 100).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const mediaUrl = (url) => url?.startsWith('//') ? `${location.protocol}${url}` : url;
  const testerPattern = /(?:^|\b)(5\s*ml|tester|sample)(?:\b|$)/i;
  const normalizedHandle = (handle) => String(handle || '').replace(/-perfume$/, '').replace(/^she-s-/, 'shes-');
  const creatorKey = (item) => String(item?.review_id || item?.id || '').match(/^VR-([^-]+)-/i)?.[1] || String(item?.review_id || item?.id || 'unknown');
  const interleaveCreators = (items) => {
    const groups = new Map();
    items.forEach((item) => {
      const key = creatorKey(item);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    const queues = [...groups].map(([key, queue]) => ({ key, queue }));
    const result = [];
    let previousKey = '';
    let cursor = 0;
    while (result.length < items.length) {
      let nextIndex = -1;
      for (let offset = 0; offset < queues.length; offset += 1) {
        const candidateIndex = (cursor + offset) % queues.length;
        if (queues[candidateIndex].queue.length && queues[candidateIndex].key !== previousKey) {
          nextIndex = candidateIndex;
          break;
        }
      }
      if (nextIndex < 0) nextIndex = queues.findIndex((group) => group.queue.length);
      if (nextIndex < 0) break;
      const next = queues[nextIndex];
      result.push(next.queue.shift());
      previousKey = next.key;
      cursor = (nextIndex + 1) % queues.length;
    }
    return result;
  };

  const icon = (name) => {
    const paths = {
      play: '<path d="M7 4.5 18 12 7 19.5z"/>',
      pause: '<path d="M7 5h3.5v14H7zm6.5 0H17v14h-3.5z"/>',
      volume: '<path d="M4 9h4l5-4v14l-5-4H4zm12.5.5a4 4 0 0 1 0 5m2-7a7 7 0 0 1 0 9" fill="none" stroke="currentColor" stroke-width="1.6"/>',
      muted: '<path d="M4 9h4l5-4v14l-5-4H4zm12 1 5 5m0-5-5 5" fill="none" stroke="currentColor" stroke-width="1.6"/>',
      fullscreen: '<path d="M5 9V5h4m6 0h4v4m0 6v4h-4m-6 0H5v-4" fill="none" stroke="currentColor" stroke-width="1.7"/>',
    };
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = paths[name];
    return svg;
  };

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return '0:00';
    return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  };

  class SorellaUgcStories {
    constructor(root) {
      this.root = root;
      const finderCampaign = document.querySelector('[data-sorella-home] .dhome__finder-campaign');
      if (finderCampaign && finderCampaign.previousElementSibling !== root) finderCampaign.before(root);
      this.track = root.querySelector('[data-ugc-track]');
      this.status = root.querySelector('[data-ugc-status]');
      this.previous = root.querySelector('[data-ugc-previous]');
      this.next = root.querySelector('[data-ugc-next]');
      this.position = root.querySelector('[data-ugc-position]');
      this.story = root.querySelector('[data-ugc-story]');
      this.storyMedia = root.querySelector('[data-ugc-story-media]');
      this.storyProducts = root.querySelector('[data-ugc-story-products]');
      this.batchSize = [3, 5].includes(Number(root.dataset.batchSize)) ? Number(root.dataset.batchSize) : 5;
      this.batch = 0;
      this.index = 0;
      this.videos = [];
      this.productCache = new Map();
      this.productCardUid = 0;
      this.lastFocus = null;
      this.loading = false;
      this.loaded = false;
      this.autoBatchTimer = null;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.previewObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!this.reducedMotion && entry.isIntersecting && entry.intersectionRatio >= .35 && this.story.hidden && !document.hidden) video.play().catch(() => {});
          else video.pause();
        });
      }, { root: this.track, threshold: [.05, .35, .75] }) : null;
      this.previous.addEventListener('click', () => this.move(-1));
      this.next.addEventListener('click', () => this.move(1));
      this.root.querySelectorAll('[data-ugc-story-close]').forEach((button) => button.addEventListener('click', () => this.closeStory()));
      this.story.addEventListener('keydown', (event) => this.onStoryKeydown(event));
      this.story.addEventListener('click', (event) => {
        if (event.target.closest('[data-add-to-cart]')) this.pendingCartFromStory = true;
      });
      this.cartDrawer = document.querySelector('[data-cart-drawer]');
      if (this.cartDrawer && 'MutationObserver' in window) {
        this.cartObserver = new MutationObserver(() => this.syncCartLayer());
        this.cartObserver.observe(this.cartDrawer, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
      }
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { this.pauseStory(); this.pausePreviews(); }
        else if (this.story.hidden) this.resumePreviews();
      });
      this.observe();
    }

    observe() {
      if (!('IntersectionObserver' in window)) return this.load();
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.load();
          observer.disconnect();
        }
      }, { rootMargin: '360px 0px' });
      observer.observe(this.root);
    }

    async load() {
      if (this.loading || this.loaded) return;
      this.loading = true;
      this.status.textContent = 'Loading Sorella stories…';
      try {
        const response = await fetch(this.root.dataset.manifestUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Story library returned ${response.status}`);
        const manifest = await response.json();
        if (this.root.dataset.consentConfirmed !== 'true' || manifest.publication_consent_confirmed !== true) {
          throw new Error('This story library is awaiting publication approval.');
        }
        const readyVideos = (manifest.videos || []).filter((item) => item.upload_status === 'ready' && item.hidden_from_storefront !== true && item.poster && item.sources?.length);
        const productHandle = normalizedHandle(this.root.dataset.productHandle);
        const productVideos = productHandle ? readyVideos.filter((item) => (item.product_handles || []).some((handle) => normalizedHandle(handle) === productHandle)) : [];
        this.videos = interleaveCreators(productHandle && productVideos.length ? productVideos : readyVideos);
        this.root.classList.toggle('sugc--global-fallback', Boolean(productHandle && !productVideos.length));
        this.loaded = true;
        if (!this.videos.length) throw new Error('The story library is still processing.');
        this.status.hidden = true;
        await this.renderBatch(false);
      } catch (error) {
        this.status.hidden = false;
        this.status.textContent = error.message;
      } finally {
        this.loading = false;
      }
    }

    async getProduct(handle) {
      if (!handle) return null;
      if (!this.productCache.has(handle)) {
        this.productCache.set(handle, (async () => {
          const candidates = [handle];
          if (handle === 'shes-bold') candidates.push('she-s-bold-perfume');
          else if (!/-perfume$/.test(handle)) candidates.push(`${handle}-perfume`);
          for (const candidate of candidates) {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(candidate)}.js`, {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            }).catch(() => null);
            if (response?.ok) return response.json();
          }
          return null;
        })());
      }
      return this.productCache.get(handle);
    }

    chooseVariant(product) {
      const available = (product?.variants || []).filter((variant) => variant.available);
      return available.find((variant) => /(^|\s)30\s*ml($|\s)/i.test(`${variant.title} ${variant.option1 || ''}`))
        || available.find((variant) => !testerPattern.test(`${variant.title} ${variant.option1 || ''}`))
        || available[0]
        || product?.variants?.[0]
        || null;
    }

    productImage(product, variant) {
      return mediaUrl(variant?.featured_image?.src || product?.featured_image || product?.images?.[0] || '');
    }

    variantLabel(variant) {
      const source = `${variant?.title || ''} ${variant?.option1 || ''}`;
      const size = source.match(/\b(5|6|8|30|50)\s*ml\b/i)?.[0]?.replace(/\s+/g, '') || '';
      if (/oil|roll|tolla/i.test(source)) return size ? `Oil · ${size}` : 'Oil';
      if (size) return size;
      return variant?.title === 'Default Title' ? 'One size' : (variant?.title || 'One size');
    }

    async batchProducts(items) {
      const handles = [...new Set(items.flatMap((item) => item.product_handles || []))];
      await Promise.all(handles.map((handle) => this.getProduct(handle)));
    }

    createCard(item, absoluteIndex) {
      const card = document.createElement('article');
      card.className = 'sugc-card';
      card.dataset.ugcCreator = creatorKey(item);
      card.setAttribute('role', 'listitem');
      card.tabIndex = 0;
      card.setAttribute('aria-label', `Open story ${absoluteIndex + 1} of ${this.videos.length}, featuring ${item.product_names.join(', ')}`);

      const preview = document.createElement('video');
      preview.className = 'sugc-card__preview';
      preview.autoplay = !this.reducedMotion;
      preview.defaultMuted = true;
      preview.muted = true;
      preview.loop = true;
      preview.playsInline = true;
      preview.preload = 'metadata';
      preview.poster = item.poster;
      preview.tabIndex = -1;
      preview.setAttribute('aria-hidden', 'true');
      preview.setAttribute('controlslist', 'nodownload noremoteplayback');
      const previewSource = item.sources
        .filter((source) => source.format !== 'm3u8')
        .sort((left, right) => Number(left.width || 0) - Number(right.width || 0))[0];
      if (previewSource) {
        const source = document.createElement('source');
        source.src = previewSource.url;
        source.type = previewSource.mime_type || 'video/mp4';
        preview.append(source);
      }

      const badge = document.createElement('span');
      badge.className = 'sugc-card__badge';
      badge.textContent = 'Sorella Moments';

      const content = document.createElement('div');
      content.className = 'sugc-card__content';
      const title = document.createElement('h3');
      title.textContent = item.product_names[0] || 'A Sorella story';
      const detail = document.createElement('p');
      detail.textContent = item.product_names.length > 1 ? `Featuring ${item.product_names.length} scents` : 'Tap to watch & shop';
      content.append(title, detail);

      const play = document.createElement('button');
      play.type = 'button';
      play.className = 'sugc-card__play';
      play.setAttribute('aria-label', `Watch and shop ${item.product_names.join(', ')}`);
      play.append(icon('play'));

      const activate = () => this.openStory(item, absoluteIndex, play);
      card.addEventListener('click', (event) => { if (!event.target.closest('button')) activate(); });
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(); }
      });
      play.addEventListener('click', activate);
      card.append(preview, badge, content, play);
      if (this.previewObserver) this.previewObserver.observe(preview);
      else if (!this.reducedMotion) preview.play().catch(() => {});

      this.getProduct(item.product_handles?.[0]).then((product) => {
        if (!product || !card.isConnected) return;
        const variant = this.chooseVariant(product);
        const imageUrl = this.productImage(product, variant);
        if (imageUrl) {
          const image = document.createElement('img');
          image.className = 'sugc-card__product-thumb';
          image.src = imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          card.append(image);
        }
        if (item.product_handles.length > 1) {
          const count = document.createElement('span');
          count.className = 'sugc-card__count';
          count.textContent = `+${item.product_handles.length - 1}`;
          card.append(count);
        }
        if (variant) detail.textContent = `${money(variant.price)} · Tap to watch & shop`;
      });
      return card;
    }

    createProductCard(product, fallbackName) {
      const variant = this.chooseVariant(product);
      const variants = product?.variants || [];
      const card = document.createElement('article');
      card.className = `sugc-product${product ? '' : ' sugc-product--unavailable'}`;
      const image = document.createElement('img');
      image.className = 'sugc-product__image';
      const imageUrl = product ? this.productImage(product, variant) : '';
      if (imageUrl) image.src = imageUrl;
      else image.hidden = true;
      image.alt = product?.title || fallbackName;

      const copy = document.createElement('div');
      copy.className = 'sugc-product__copy';
      const title = document.createElement('h4');
      title.textContent = product?.title || fallbackName;
      copy.append(title);

      if (variant) {
        const options = document.createElement('fieldset');
        options.className = 'sugc-product__variants';
        const legend = document.createElement('legend');
        legend.textContent = 'Choose size';
        const optionList = document.createElement('div');
        const groupName = `SorellaUgcVariant-${++this.productCardUid}`;
        variants.forEach((candidate) => {
          const option = document.createElement('label');
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = groupName;
          input.value = candidate.id;
          input.checked = String(candidate.id) === String(variant.id);
          input.disabled = !candidate.available;
          const chip = document.createElement('span');
          chip.textContent = `${this.variantLabel(candidate)}${candidate.available ? '' : ' · Sold out'}`;
          option.append(input, chip);
          optionList.append(option);
        });
        options.append(legend, optionList);

        const buy = document.createElement('div');
        buy.className = 'sugc-product__buy';
        const price = document.createElement('span');
        price.className = 'sugc-product__price';
        price.textContent = money(variant.price);
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'sugc-product__add';
        add.dataset.addToCart = '';
        add.dataset.variantId = variant.id;
        add.disabled = !variant.available;
        const label = document.createElement('span');
        label.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
        add.append(label);
        buy.append(price, add);
        const error = document.createElement('p');
        error.className = 'sugc-product__error';
        error.dataset.addError = '';
        error.setAttribute('role', 'status');
        optionList.addEventListener('change', (event) => {
          const input = event.target.closest('input[type="radio"]');
          if (!input) return;
          const selected = variants.find((candidate) => String(candidate.id) === input.value);
          if (!selected) return;
          add.dataset.variantId = selected.id;
          add.disabled = !selected.available;
          label.textContent = selected.available ? 'ADD TO CART' : 'SOLD OUT';
          price.textContent = money(selected.price);
          const selectedImage = this.productImage(product, selected);
          if (selectedImage) {
            image.src = selectedImage;
            image.hidden = false;
          }
          error.textContent = '';
        });
        copy.append(options, buy, error);
      } else {
        const unavailable = document.createElement('p');
        unavailable.textContent = 'Currently unavailable';
        copy.append(unavailable);
      }
      card.append(image, copy);
      return card;
    }

    createMedia(item) {
      const video = document.createElement('video');
      video.playsInline = true;
      video.preload = 'metadata';
      video.poster = item.poster;
      video.setAttribute('controlslist', 'nodownload noremoteplayback');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('aria-label', item.alt || `Sorella story featuring ${item.product_names.join(', ')}`);
      item.sources
        .filter((source) => source.format !== 'm3u8')
        .sort((left, right) => Number(right.width || 0) - Number(left.width || 0))
        .slice(0, 2)
        .forEach((sourceItem) => {
        const source = document.createElement('source');
        source.src = sourceItem.url;
        source.type = sourceItem.mime_type || 'video/mp4';
        video.append(source);
        });

      const controls = document.createElement('div');
      controls.className = 'sugc-story__media-controls';
      const play = document.createElement('button');
      play.type = 'button';
      play.setAttribute('aria-label', 'Pause video');
      play.append(icon('pause'));
      const progress = document.createElement('input');
      progress.type = 'range';
      progress.min = '0';
      progress.max = '1000';
      progress.value = '0';
      progress.setAttribute('aria-label', 'Video progress');
      const time = document.createElement('span');
      time.className = 'sugc-story__time';
      time.textContent = `0:00 / ${formatTime(item.duration_seconds)}`;
      const mute = document.createElement('button');
      mute.type = 'button';
      mute.setAttribute('aria-label', 'Mute video');
      mute.append(icon('volume'));
      const fullscreen = document.createElement('button');
      fullscreen.type = 'button';
      fullscreen.setAttribute('aria-label', 'View full screen');
      fullscreen.append(icon('fullscreen'));

      play.addEventListener('click', async () => {
        if (video.paused) await video.play().catch(() => {}); else video.pause();
      });
      progress.addEventListener('input', () => {
        if (Number.isFinite(video.duration)) video.currentTime = (Number(progress.value) / 1000) * video.duration;
      });
      mute.addEventListener('click', () => {
        video.muted = !video.muted;
        mute.replaceChildren(icon(video.muted ? 'muted' : 'volume'));
        mute.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      });
      fullscreen.addEventListener('click', () => {
        if (this.storyMedia.requestFullscreen) this.storyMedia.requestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
      });
      video.addEventListener('play', () => { play.replaceChildren(icon('pause')); play.setAttribute('aria-label', 'Pause video'); });
      video.addEventListener('pause', () => { play.replaceChildren(icon('play')); play.setAttribute('aria-label', 'Play video'); });
      video.addEventListener('timeupdate', () => {
        if (Number.isFinite(video.duration) && video.duration > 0) progress.value = String(Math.round((video.currentTime / video.duration) * 1000));
        time.textContent = `${formatTime(video.currentTime)} / ${formatTime(video.duration || item.duration_seconds)}`;
      });
      video.addEventListener('ended', () => { video.currentTime = 0; });
      controls.append(play, progress, time, mute, fullscreen);
      this.storyMedia.append(video, controls);
      requestAnimationFrame(() => video.play().catch(() => {}));
    }

    async openStory(item, absoluteIndex, trigger) {
      this.lastFocus = trigger || document.activeElement;
      this.index = absoluteIndex - this.batch * this.batchSize;
      this.updateNavigation();
      this.story.hidden = false;
      document.body.classList.add('sugc-story-open');
      this.pausePreviews();
      this.storyMedia.replaceChildren();
      this.storyProducts.replaceChildren();
      this.createMedia(item);
      item.product_handles.forEach((handle, index) => {
        const loading = document.createElement('article');
        loading.className = 'sugc-product sugc-product--loading';
        loading.setAttribute('aria-label', `Loading ${item.product_names[index]}`);
        this.storyProducts.append(loading);
        this.getProduct(handle).then((product) => loading.replaceWith(this.createProductCard(product, item.product_names[index])));
      });
      this.story.querySelector('[data-ugc-story-close]:not(.sugc-story__backdrop)')?.focus();
    }

    pauseStory() { this.storyMedia?.querySelector('video')?.pause(); }

    pausePreviews() { this.track.querySelectorAll('.sugc-card__preview').forEach((video) => video.pause()); }

    resumePreviews() {
      if (this.reducedMotion || document.hidden) return;
      this.track.querySelectorAll('.sugc-card__preview').forEach((video) => {
        const trackRect = this.track.getBoundingClientRect();
        const rect = video.getBoundingClientRect();
        const visibleWidth = Math.max(0, Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left));
        if (visibleWidth >= rect.width * .35) video.play().catch(() => {});
      });
    }

    syncCartLayer() {
      const cartIsOpen = this.cartDrawer?.classList.contains('is-open') || this.cartDrawer?.getAttribute('aria-hidden') === 'false';
      document.body.classList.toggle('sugc-cart-over-story', Boolean(!this.story.hidden && cartIsOpen));
      if (!cartIsOpen) this.pendingCartFromStory = false;
    }

    closeStory() {
      if (this.story.hidden) return;
      const video = this.storyMedia.querySelector('video');
      if (video) {
        video.pause();
        video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        video.load();
      }
      this.storyMedia.replaceChildren();
      this.story.hidden = true;
      document.body.classList.remove('sugc-story-open');
      document.body.classList.remove('sugc-cart-over-story');
      this.resumePreviews();
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    onStoryKeydown(event) {
      if (event.key === 'Escape') return this.closeStory();
      if (event.key !== 'Tab') return;
      const focusable = [...this.story.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled)')].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    onScroll() {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        const cards = [...this.track.children];
        if (!cards.length) return;
        this.index = cards.reduce((nearest, card, index) => {
          const distance = Math.abs(card.offsetLeft - this.track.scrollLeft - this.track.offsetLeft);
          return distance < nearest.distance ? { index, distance } : nearest;
        }, { index: 0, distance: Infinity }).index;
        this.updateNavigation();
        const reachedEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 8;
        const maxBatch = Math.ceil(this.videos.length / this.batchSize) - 1;
        clearTimeout(this.autoBatchTimer);
        if (reachedEnd && this.batch < maxBatch) {
          this.autoBatchTimer = setTimeout(() => {
            this.batch += 1;
            this.index = 0;
            this.renderBatch(false);
          }, 700);
        }
      }, 90);
    }

    async renderBatch(focus = true) {
      clearTimeout(this.autoBatchTimer);
      this.previewObserver?.disconnect();
      this.track.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        video.load();
      });
      const start = this.batch * this.batchSize;
      const items = this.videos.slice(start, start + this.batchSize);
      this.track.replaceChildren();
      items.forEach((item, index) => this.track.append(this.createCard(item, start + index)));
      this.index = Math.min(this.index, Math.max(0, items.length - 1));
      this.track.scrollLeft = 0;
      this.updateNavigation();
      this.batchProducts(items);
      if (focus) this.track.children[this.index]?.focus({ preventScroll: true });
    }

    move(direction) {
      if (!this.videos.length) return;
      clearTimeout(this.autoBatchTimer);
      const start = this.batch * this.batchSize;
      const count = Math.min(this.batchSize, this.videos.length - start);
      const requested = this.index + direction;
      if (requested >= 0 && requested < count) {
        this.index = requested;
        const card = this.track.children[this.index];
        card?.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
        card?.focus({ preventScroll: true });
        this.updateNavigation();
        return;
      }
      const nextBatch = this.batch + direction;
      const maxBatch = Math.ceil(this.videos.length / this.batchSize) - 1;
      if (nextBatch < 0 || nextBatch > maxBatch) return;
      this.batch = nextBatch;
      this.index = direction > 0 ? 0 : Math.min(this.batchSize, this.videos.length - nextBatch * this.batchSize) - 1;
      this.renderBatch();
    }

    updateNavigation() {
      const absolute = this.batch * this.batchSize + this.index;
      this.previous.disabled = absolute <= 0;
      this.next.disabled = absolute >= this.videos.length - 1;
      this.position.textContent = this.videos.length ? `${absolute + 1} / ${this.videos.length}` : '—';
    }
  }

  const initialize = (scope = document) => scope.querySelectorAll('[data-sorella-ugc]:not([data-ugc-ready])').forEach((root) => {
    root.dataset.ugcReady = 'true';
    new SorellaUgcStories(root);
  });

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
